import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useResume } from '../../context/ResumeContext';
import AIKeywordAnalysis from '../../components/resume/AIKeywordAnalysis';
import { useAuth } from '../../context/AuthContext';
import LoginModal from '../../components/auth/LoginModal';
import UpgradeModal from '../../components/billing/UpgradeModal';
import { generateTex } from '../../latex/jakeTemplate';
import { buildTexData } from '../../latex/buildTexData';
import { useLatexEngine } from '../../latex/useLatexEngine';
import PdfPreview from '../../components/resume/PdfPreview';
import SectionReorderPanel from '../../components/resume/SectionReorderPanel';
import * as pdfjsLib from 'pdfjs-dist';

// Ensure pdf.js worker is set (PdfPreview already sets this globally, but guard here too)
if (typeof window !== 'undefined') {
    pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.mjs`;
}

async function saveThumbnail(bytes, resumeId) {
    try {
        const doc = await pdfjsLib.getDocument({ data: bytes.slice(0) }).promise;
        const page = await doc.getPage(1);
        const viewport = page.getViewport({ scale: 0.5 });
        const canvas = document.createElement('canvas');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
        const thumbnail = canvas.toDataURL('image/jpeg', 0.7);
        await fetch('https://renaisons.com/api/save_thumbnail.php', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ resume_id: resumeId, thumbnail }),
        });
        console.log('[thumbnail] saved for resume', resumeId);
    } catch (e) {
        console.error('[thumbnail] capture/save error:', e);
    }
}

const FinalResumePage = () => {
    const { user, isPremium } = useAuth();
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
    const [pendingAction, setPendingAction] = useState(null);
    const {
        contact, setContact, contactToggles,
        summary, setSummary,
        experiences, setExperiences,
        educations, setEducations,
        certifications, setCertifications,
        awards, setAwards,
        skills, setSkills,
        projects, setProjects,
    } = useResume();

    const navigate = useNavigate();
    const { resumeId } = useParams();

    // --- Section order state ---
    const [sectionOrder, setSectionOrder] = useState([
        'summary', 'experience', 'education', 'projects', 'certifications', 'awards', 'skills'
    ]);

    // --- LaTeX engine ---
    const { compile, ready, compiling, error } = useLatexEngine();
    const [pdf, setPdf] = useState(null);
    const [compileLog, setCompileLog] = useState('');
    const [logExpanded, setLogExpanded] = useState(false);
    const latestTexRef = useRef('');

    // --- Debounced recompile when data or section order changes ---
    useEffect(() => {
        if (!ready) return;
        const tex = generateTex(buildTexData({ contact, contactToggles, summary, skills, experiences, educations, projects, awards, certifications }), { sections: sectionOrder });
        latestTexRef.current = tex;
        const t = setTimeout(async () => {
            try {
                const r = await compile(tex);
                if (latestTexRef.current !== tex) return; // stale
                if (r.pdf) {
                    setPdf(r.pdf);
                    setCompileLog('');
                } else {
                    setCompileLog(r.log);
                }
            } catch (e) {
                if (latestTexRef.current === tex) setCompileLog(e?.message || String(e));
            }
        }, 300);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, sectionOrder, contact, contactToggles, summary, skills, experiences, educations, projects, awards, certifications]);

    // --- Debounced thumbnail after successful compile ---
    useEffect(() => {
        if (!pdf || !resumeId) return;
        const t = setTimeout(() => {
            saveThumbnail(pdf, resumeId);
        }, 1500);
        return () => clearTimeout(t);
    }, [pdf, resumeId]);

    // --- Fetch resume data ---
    useEffect(() => {
        if (resumeId) {
            fetch(`https://renaisons.com/api/get_resume_details.php?resume_id=${resumeId}`, {
                credentials: 'include'
            })
                .then(res => res.json())
                .then(result => {
                    if (result.status === 'success' && result.data) {
                        const { data: d } = result;
                        setExperiences(d.experiences || []);
                        setProjects(d.projects || []);
                        setEducations(d.educations || []);
                        setCertifications(d.certifications || []);
                        setAwards(d.awards || []);
                        setSummary(d.summary?.summaries_description || '');
                        setSkills(d.skills?.skills_description || '');
                        const contactInfo = d.contact_info || {};
                        contactInfo.fullName = contactInfo.full_name || d.resume_name || '';
                        setContact(contactInfo);
                    }
                })
                .catch(err => console.error(err));
        }
    }, [resumeId, setContact, setExperiences, setProjects, setEducations, setSummary, setCertifications, setAwards, setSkills]);

    // --- Navigation ---
    const handleBackToEditor = () => {
        if (!user) {
            setPendingAction('back');
            setIsLoginModalOpen(true);
            return;
        }
        navigate(`/resume/${resumeId}/contact`);
    };

    // --- Gated download ---
    const handleDownloadPDF = async () => {
        if (!user) { setPendingAction('download'); setIsLoginModalOpen(true); return; }
        if (!isPremium) { setIsUpgradeModalOpen(true); return; }
        let bytes = pdf;
        if (!bytes) {
            try {
                const r = await compile(generateTex(buildTexData({ contact, contactToggles, summary, skills, experiences, educations, projects, awards, certifications }), { sections: sectionOrder }));
                bytes = r.pdf;
            } catch (e) {
                setCompileLog(e?.message || String(e));
                return;
            }
        }
        if (!bytes) { setCompileLog('Could not generate PDF.'); return; }
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${(contact.fullName || 'resume').replace(/\s+/g, '_')}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <div className="text-white min-h-screen p-4 sm:p-8">
            {/* Login Modal */}
            <LoginModal
                isOpen={isLoginModalOpen}
                onClose={() => setIsLoginModalOpen(false)}
                onLoginSuccess={() => {
                    setIsLoginModalOpen(false);
                    if (pendingAction === 'download') {
                        handleDownloadPDF();
                    } else if (pendingAction === 'back') {
                        navigate(`/resume/${resumeId}/contact`);
                    }
                    setPendingAction(null);
                }}
            />
            {/* Upgrade Modal */}
            <UpgradeModal
                open={isUpgradeModalOpen}
                onClose={() => setIsUpgradeModalOpen(false)}
                reason="download_locked"
            />

            <div className="max-w-[1400px] mx-auto">
                {/* Top bar */}
                <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
                    <div className="flex items-center gap-4">
                        <button onClick={handleBackToEditor} className="inline-flex items-center gap-2 text-sm font-medium text-neutral-300 hover:text-white bg-white/5 hover:bg-white/10 border border-white/10 py-2 px-4 rounded-lg transition-colors">
                            <span aria-hidden>&larr;</span> Back to Editor
                        </button>
                        <div className="hidden sm:flex items-center gap-2 text-xs text-neutral-500">
                            {compiling ? (
                                <><span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" /> Compiling…</>
                            ) : (
                                <><span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> Preview up to date</>
                            )}
                        </div>
                    </div>
                    {user && !isPremium ? (
                        <button
                            onClick={handleDownloadPDF}
                            title="Upgrade to Premium to download"
                            className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/10 text-white font-semibold py-2 px-5 rounded-lg transition-colors"
                        >
                            <span aria-hidden>&#128274;</span> Download PDF
                            <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-400 text-black px-1.5 py-0.5 rounded">Premium</span>
                        </button>
                    ) : (
                        <button onClick={handleDownloadPDF} className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-black font-semibold py-2 px-5 rounded-lg transition-colors shadow-lg shadow-cyan-500/20">
                            <span aria-hidden>&darr;</span> Download PDF
                        </button>
                    )}
                </div>

                {/* Engine load error */}
                {error && (
                    <div className="mb-6 p-4 bg-red-900 border border-red-600 rounded-lg text-red-200">
                        <p className="font-semibold mb-2">LaTeX engine failed to load.</p>
                        <p className="text-sm mb-3">{error.message || String(error)}</p>
                        <button
                            onClick={() => window.location.reload()}
                            className="bg-red-700 hover:bg-red-600 text-white text-sm font-semibold py-1.5 px-4 rounded"
                        >
                            Retry
                        </button>
                    </div>
                )}

                {/* LaTeX compile error (collapsible) */}
                {compileLog && (
                    <div className="mb-6 p-4 bg-yellow-900 border border-yellow-600 rounded-lg text-yellow-200">
                        <button
                            className="flex items-center gap-2 w-full text-left font-semibold"
                            onClick={() => setLogExpanded(x => !x)}
                        >
                            <span>{logExpanded ? '▾' : '▸'}</span>
                            LaTeX compile errors (last good preview kept)
                        </button>
                        {logExpanded && (
                            <pre className="mt-3 text-xs overflow-auto max-h-48 whitespace-pre-wrap text-yellow-100">
                                {compileLog}
                            </pre>
                        )}
                    </div>
                )}

                {/* Main layout: sticky controls + framed preview */}
                <div className="grid grid-cols-1 lg:grid-cols-[330px_minmax(0,1fr)] gap-6 items-start">
                    <div className="lg:sticky lg:top-6 space-y-5">
                        <SectionReorderPanel
                            sectionOrder={sectionOrder}
                            onReorderSections={setSectionOrder}
                        />
                        <AIKeywordAnalysis />
                    </div>

                    <div className="rounded-xl border border-white/10 bg-neutral-950/40 p-3 sm:p-6 flex justify-center min-h-[60vh]">
                        <PdfPreview pdf={pdf} compiling={compiling} />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FinalResumePage;
